import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import {capitalize} from "@mui/material/utils";
import {useTranslation} from "react-i18next";
import {useMemo, useState} from "react";
import {useMutation, useQueryClient} from "react-query";
import {useSnackbar} from "notistack";
import {Tool, ToolConfigDaten, ToolConfigKey, ToolConfigListe} from "../../api";
import {useToolConfigSchema} from "../../client/queries";
import SuspenseBoundary from "../SuspenseBoundary";
import SubmitButton from "../SubmitButton";
import type {QueryKey} from "react-query";

export interface ToolConfigHooks {
    useConfigs: () => ToolConfigListe;
    upsert: (key: string, data: ToolConfigDaten) => Promise<unknown>;
    remove: (key: string) => Promise<unknown>;
    queryKey: QueryKey;
}

interface Props {
    open: boolean;
    onClose: () => void;
    tool: Tool;
    hooks: ToolConfigHooks;
}

export default function DialogToolConfig({open, onClose, tool, hooks}: Props) {
    const {t: translation} = useTranslation("aufgaben");

    return (
        <Dialog id={`dialog-tool-config-${tool.id}`} open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle id={`dialog-tool-config-title-${tool.id}`}>
                {capitalize(translation("tool-config-dialog-title"))} — {tool.name}
            </DialogTitle>
            {open && (
                <SuspenseBoundary>
                    <DialogToolConfigContent tool={tool} hooks={hooks} onClose={onClose}/>
                </SuspenseBoundary>
            )}
        </Dialog>
    );
}

function DialogToolConfigContent({tool, hooks, onClose}: { tool: Tool; hooks: ToolConfigHooks; onClose: () => void }) {
    const {t: translation} = useTranslation("aufgaben");
    const {enqueueSnackbar} = useSnackbar();
    const queryClient = useQueryClient();
    const schema = useToolConfigSchema(tool.id);
    const configs = hooks.useConfigs();

    const initialValues = useMemo(() => {
        const map: Record<string, string> = {};
        schema.keys.forEach(k => {
            const existing = configs.items.find(c => c.key === k.key);
            map[k.key] = existing?.value ?? "";
        });
        return map;
    }, [schema, configs]);

    const [values, setValues] = useState<Record<string, string>>(initialValues);

    const {mutateAsync, isLoading} = useMutation(
        async () => {
            await Promise.all(schema.keys.map(async k => {
                const current = values[k.key] ?? "";
                const original = initialValues[k.key] ?? "";
                if (current === original) return;
                if (current === "") {
                    const existing = configs.items.find(c => c.key === k.key);
                    if (existing) await hooks.remove(k.key);
                } else {
                    await hooks.upsert(k.key, {value: current});
                }
            }));
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(hooks.queryKey);
                enqueueSnackbar(capitalize(translation("tool-config-save-successful")), {variant: "success"});
                onClose();
            },
            onError: () => {
                enqueueSnackbar(capitalize(translation("tool-config-save-failed")), {variant: "error"});
            },
        }
    );

    return (
        <>
            <DialogContent id={`dialog-tool-config-content-${tool.id}`}>
                {schema.keys.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {translation("tool-config-no-keys")}
                    </Typography>
                )}
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {schema.keys.map(k => (
                        <ToolConfigField
                            key={k.key}
                            schemaKey={k}
                            value={values[k.key] ?? ""}
                            onChange={v => setValues(prev => ({...prev, [k.key]: v}))}
                        />
                    ))}
                </Box>
            </DialogContent>
            <DialogActions id={`dialog-tool-config-actions-${tool.id}`}>
                <Button onClick={onClose} color="inherit">
                    {capitalize(translation("abort"))}
                </Button>
                <SubmitButton
                    id={`dialog-tool-config-submit-${tool.id}`}
                    type="button"
                    variant="contained"
                    color="secondary"
                    loading={isLoading}
                    onClick={() => mutateAsync()}
                >
                    {capitalize(translation("save"))}
                </SubmitButton>
            </DialogActions>
        </>
    );
}

function ToolConfigField({schemaKey, value, onChange}: { schemaKey: ToolConfigKey; value: string; onChange: (v: string) => void }) {
    const {t: translation} = useTranslation("aufgaben");
    const helperText = [
        schemaKey.description,
        schemaKey.defaultValue ? `${translation("tool-config-key-default-value")}: ${schemaKey.defaultValue}` : null,
    ].filter(Boolean).join(" · ");

    if (schemaKey.type === "BOOLEAN") {
        return (
            <FormControlLabel
                control={<Switch checked={value === "true"} onChange={e => onChange(e.target.checked ? "true" : "false")}/>}
                label={`${schemaKey.key}${schemaKey.required ? " *" : ""}`}
            />
        );
    }
    return (
        <TextField
            label={`${schemaKey.key}${schemaKey.required ? " *" : ""}`}
            value={value}
            onChange={e => onChange(e.target.value)}
            helperText={helperText || undefined}
            required={schemaKey.required}
            type={schemaKey.type === "NUMBER" ? "number" : "text"}
            inputProps={schemaKey.type === "NUMBER" ? {min: schemaKey.min ?? undefined, max: schemaKey.max ?? undefined} : undefined}
            multiline={schemaKey.multiline}
            minRows={schemaKey.multiline ? 4 : undefined}
            maxRows={schemaKey.multiline ? 12 : undefined}
            fullWidth
        />
    );
}
